import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LOGO_URL = 'https://greenworldil.com/wp-content/uploads/2025/06/GW-Logo-100w.png';
const PHONE = '(708) 698-4072';
const PHONE_HREF = 'tel:7086984072';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about' },
  { label: 'Locations', href: '/locations' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  return (
    <>
      {/* Top info bar */}
      <div className="bg-brand-green text-white text-xs py-2 px-6 hidden lg:flex items-center justify-between">
        <div className="flex items-center gap-6">
          <span className="flex items-center gap-1.5">
            <i className="ri-map-pin-2-line text-brand-gold" />
            5801 N Northwest Hwy, Chicago, IL 60631
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line text-brand-gold" />
            Mon–Fri 6:30AM–6PM &nbsp;|&nbsp; Sat 8AM–2PM
          </span>
        </div>
        <a href={`mailto:Greenworldroofing@gmail.com`} className="flex items-center gap-1.5 hover:text-brand-gold transition-colors">
          <i className="ri-mail-line text-brand-gold" />
          Greenworldroofing@gmail.com
        </a>
      </div>

      {/* Main navbar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white shadow-md top-0'
            : 'bg-transparent top-7 lg:top-9'
        }`}
        style={{ top: scrolled ? 0 : undefined }}
      >
        <nav className="flex items-center justify-between px-6 lg:px-12 py-4">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <div className="w-36 h-12 flex items-center justify-center">
              <img
                src={LOGO_URL}
                alt="Green World Construction and Roofing"
                className="h-10 w-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop nav links */}
          <ul className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <li key={link.href}>
                <Link
                  to={link.href}
                  className={`text-sm font-semibold tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
                    scrolled
                      ? isActive(link.href)
                        ? 'text-brand-green'
                        : 'text-brand-dark hover:text-brand-green'
                      : isActive(link.href)
                      ? 'text-brand-gold'
                      : 'text-white hover:text-brand-gold'
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={PHONE_HREF}
              className={`flex items-center gap-2 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-brand-dark hover:text-brand-green' : 'text-white hover:text-brand-gold'
              }`}
            >
              <div className={`w-5 h-5 flex items-center justify-center ${scrolled ? '' : ''}`}>
                <i className={`ri-phone-fill text-sm ${scrolled ? 'text-brand-green' : 'text-brand-gold'}`} />
              </div>
              {PHONE}
            </a>
            <Link
              to="/contact"
              className="bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold px-5 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              Free Inspection
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className={`lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${
              scrolled ? 'text-brand-dark' : 'text-white'
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
          </button>
        </nav>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-brand-cream-3">
            <div className="px-6 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`py-3 text-sm font-semibold border-b border-brand-cream-2 cursor-pointer ${
                    isActive(link.href) ? 'text-brand-green' : 'text-brand-dark hover:text-brand-green'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href={PHONE_HREF}
                className="mt-3 flex items-center gap-2 bg-brand-green text-white text-sm font-bold px-5 py-3 rounded-md justify-center cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill" />
                Call {PHONE}
              </a>
              <Link
                to="/contact"
                className="mt-2 flex items-center gap-2 bg-brand-gold text-white text-sm font-bold px-5 py-3 rounded-md justify-center cursor-pointer whitespace-nowrap"
              >
                Get Free Inspection
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Mobile sticky call bar */}
      <a
        href={PHONE_HREF}
        className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-brand-green text-white text-sm font-bold py-4 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
      >
        <i className="ri-phone-fill text-brand-gold text-base" />
        Call Now: {PHONE}
      </a>
    </>
  );
}
